import { useState } from 'react';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  EventType,
  Timeline,
  TimelineEvent,
  TimelineEventAction,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  DatePickerWithRange,
  LoadingSpinner,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { DateRange } from 'react-day-picker';
import { PiListBullets, PiChartBar } from 'react-icons/pi';
import { PortCallListView, PortCallListMode } from './PortCallListView';

type ViewMode = 'timeline' | 'list';

const getStoredViewMode = (storageKey: string): ViewMode => {
  try {
    const stored = localStorage.getItem(storageKey);
    return stored === 'list' ? 'list' : 'timeline';
  } catch {
    return 'timeline';
  }
};

export type TimelineContainerProps = {
  isLoading: boolean;
  events: TimelineEvent[];
  eventTypes: EventType[]; // All possible event types
  className?: string;
  clusterThreshold?: number; // Distance threshold for clustering (in percentage)
  showLegend?: boolean; // Whether to show the legend
  title: string; // Title for the timeline
  dateRange?: DateRange | undefined;
  onDateRangeChange?: (dateRange: DateRange | undefined) => void;
  emptyMessage?: string; // Message to show when there are no events or an error occurred
  eventActions?: TimelineEventAction[]; // Optional array of actions for events
  listMode?: PortCallListMode; // When provided, enables the list view toggle
  viewModeStorageKey?: string; // Unique key for persisting view mode per instance
};

export const TimelineContainer = ({
  isLoading,
  events,
  eventTypes,
  className,
  clusterThreshold = 5,
  showLegend = true,
  title,
  dateRange,
  onDateRangeChange,
  emptyMessage,
  eventActions,
  listMode,
  viewModeStorageKey = 'timeline-view-mode',
}: TimelineContainerProps) => {
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    getStoredViewMode(viewModeStorageKey),
  );

  const toggleViewMode = (mode: ViewMode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(viewModeStorageKey, mode);
    } catch {
      // Ignore storage errors
    }
  };

  // Check if date range is not selected
  const noDateRangeSelected = !dateRange?.from && !dateRange?.to;

  return (
    <div>
      <Card className="p-4 space-y-2 min-h-[542px]">
        <CardHeader>
          <div className="flex justify-between w-full">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center gap-2">
                <CardTitle>{title}</CardTitle>
                {listMode && (
                  <div className="flex items-center border rounded-md">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn(
                            'size-7 rounded-r-none',
                            viewMode === 'timeline' && 'bg-accent',
                          )}
                          onClick={() => toggleViewMode('timeline')}
                        >
                          <PiChartBar className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Timeline view</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn(
                            'size-7 rounded-l-none',
                            viewMode === 'list' && 'bg-accent',
                          )}
                          onClick={() => toggleViewMode('list')}
                        >
                          <PiListBullets className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>List view</TooltipContent>
                    </Tooltip>
                  </div>
                )}
              </div>
              {onDateRangeChange && (
                <div className="w-80">
                  <DatePickerWithRange
                    date={dateRange}
                    setDate={onDateRangeChange}
                  />
                </div>
              )}
            </div>
            {showLegend && viewMode === 'timeline' && (
              <div className="pb-10 flex flex-wrap gap-4 justify-center pr-4">
                {eventTypes.map((type) => (
                  <Tooltip key={type.id}>
                    <TooltipTrigger>
                      <div
                        key={type.id}
                        className={cn(
                          'flex items-center gap-2 cursor-default',
                          type.disabled && 'opacity-50',
                        )}
                      >
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: type.color }}
                        />
                        <span className="text-sm">{type.name}</span>
                      </div>
                    </TooltipTrigger>
                    {type.disabled && (
                      <TooltipContent>Missing permission</TooltipContent>
                    )}
                  </Tooltip>
                ))}
              </div>
            )}
          </div>
        </CardHeader>
        {isLoading ? (
          <Card>
            <CardHeader className="min-h-[112px]">
              <div className="flex space-x-2 items-center justify-center py-36 h-full">
                <LoadingSpinner></LoadingSpinner>
              </div>
            </CardHeader>
          </Card>
        ) : noDateRangeSelected ? (
          <div className="flex-1 flex items-center justify-center w-full text-muted-foreground">
            Choose a date range to view timeline events.
          </div>
        ) : events.length === 0 && emptyMessage ? (
          <div className="flex-1 flex items-center justify-center w-full text-muted-foreground">
            {emptyMessage}
          </div>
        ) : listMode && viewMode === 'list' ? (
          <PortCallListView
            events={events}
            mode={listMode}
            eventActions={eventActions}
            className={className}
          />
        ) : (
          <Timeline
            events={events}
            eventTypes={eventTypes}
            className={className}
            clusterThreshold={clusterThreshold}
            dateRange={dateRange}
            eventActions={eventActions}
          />
        )}
      </Card>
    </div>
  );
};
